import React, { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";

import { Stack, useMediaQuery } from "@mui/material";

import { InfoLink } from "@components/InfoStack/components";
import styled from "styled-components";

import AudioSanctumLogo from "@/assets/svg/logos/smal-logo_ico.svg";
import AccountRestoreModal from "@/components/AccountRestoreModal";
import { ScrollChevron, ScrollHint, useScrollAffordance } from "@/components/CartModal/phoneTemplate";
import GradientDialog from "@/components/Dialog";
import FirstStep from "@/components/RecoveryDialog/components/FirstStep";
import SecondStep from "@/components/RecoveryDialog/components/SecondStep";
import ThirdStep from "@/components/RecoveryDialog/components/ThirdStep";
import { LOGIN_NOTICE_KEY } from "@/utils/constants";

import {
  AuthSlidePanel,
  AuthSliderTrack,
  AuthSliderViewport,
  DialogTitleTypography,
  Logo,
} from "./components";
import Form from "./Form";

/* "Scroll ⌄" affordance for phone/tablet — the auth modal scrolls the whole
   paper when the form is taller than the screen (esp. signup with the captcha).
   Sticky to the paper's visible bottom; only RENDERED (display) while there's
   more below — so when the form fits it adds no trailing gap, and when it
   overflows it pins to the bottom edge. (A negative margin here pushes the
   sticky box off-screen — don't.) */
const AuthScrollFade = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  height: 52px;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 6px;
  background: linear-gradient(to bottom, rgba(111, 83, 60, 0) 0%, rgba(111, 83, 60, 0.85) 55%, rgba(111, 83, 60, 1) 100%);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.25s ease;
`;

interface ILoginDialog {
  open: boolean;
  handleClose: () => void;
  authPanel: number;
  setAuthPanel: (panel: number) => void;
  resetToken: string | null;
  isClosable: boolean;
  /** Green notice above the form, supplied by the layout from `/?login=<code>`.
      Takes precedence over the legacy sessionStorage key: the URL survives the
      re-mounts that made the storage handshake unreliable. */
  loginNotice?: string;
}

const PANEL_TITLES = [
  "accountLogin",
  "createAccount",
  "passwordRecovery.firstStep.mainText",
  "passwordRecovery.secondStep.mainText",
  "passwordRecovery.thirdStep.mainText",
];

const LoginDialog: FC<ILoginDialog> = ({
  open,
  handleClose,
  authPanel,
  setAuthPanel,
  resetToken,
  isClosable,
  loginNotice: loginNoticeProp,
}) => {
  const { t } = useTranslation("login");

  // Phone: let the whole modal scroll (like our other modals) instead of
  // clipping. The create-account form (logo + title + name/email/password +
  // terms + button) is taller than a small phone screen, and the locked
  // no-scroll mode cut off the bottom fields. Desktop/tablet keep no-scroll.
  const isPhone = useMediaQuery("(max-width:600px)");
  const isMobileOrTablet = useMediaQuery("(max-width:1023px)");

  /* ── One-time login notice (e.g. "Your password has been changed,
       please log in with your new password.") ──
       Two pathways feed this state:
       1. The user lands on the page logged-out with the notice already
          stashed in sessionStorage (e.g. after an authed-user password
          change redirected them to /). Read once on mount.
       2. The user just completed the forgot-password reset INSIDE this
          dialog and we want to surface the notice on panel 0. The
          recovery Form sets sessionStorage then handleNextStep below
          calls `refreshLoginNoticeFromStorage` to pick it up.
       Both pathways read+clear sessionStorage atomically so the notice
       only shows once. Form receives `loginNotice` as a prop and renders
       it directly — no internal sessionStorage reading there. */
  // Lazy initial value: peek sessionStorage but do NOT remove yet. The
  // problem with consuming in the useState initializer is that this
  // component mounts more than once (re-mounts during navigation /
  // potentially Fast Refresh), and the first mount would eat the value
  // before the visible mount needed it.
  const [loginNotice, setLoginNoticeState] = useState<string>(() => {
    try {
      return sessionStorage.getItem(LOGIN_NOTICE_KEY) || "";
    } catch { return ""; }
  });
  // The URL-supplied notice wins. It is re-derived from the query param on
  // every mount, so unlike the sessionStorage value it cannot be lost to a
  // re-mount part-way through the flow.
  const effectiveNotice = loginNoticeProp || loginNotice;

  // Only consume sessionStorage when the modal actually OPENS — that's
  // the visible moment we want the banner for. Multiple ghost mounts of
  // this component (closed) no longer affect the one-shot nature.
  useEffect(() => {
    if (!open) return;
    try {
      const v = sessionStorage.getItem(LOGIN_NOTICE_KEY);
      if (v) {
        sessionStorage.removeItem(LOGIN_NOTICE_KEY);
        if (v !== loginNotice) setLoginNoticeState(v);
      }
    } catch { /* sessionStorage blocked — noop */ }
    // Intentionally only depends on `open`. `loginNotice` is read inside
    // to compare but doesn't need to retrigger the effect.
    // eslint-disable-next-line
  }, [open]);
  const refreshLoginNoticeFromStorage = () => {
    try {
      const v = sessionStorage.getItem(LOGIN_NOTICE_KEY);
      if (v) {
        sessionStorage.removeItem(LOGIN_NOTICE_KEY);
        setLoginNoticeState(v);
      }
    } catch { /* sessionStorage blocked — noop */ }
  };

  /* ── Slide animation ── */
  const [displayStep, setDisplayStep] = useState(authPanel);
  const [skipTransition, setSkipTransition] = useState(false);
  const prevPanelRef = useRef(authPanel);

  useEffect(() => {
    const prev = prevPanelRef.current;
    prevPanelRef.current = authPanel;
    const diff = Math.abs(authPanel - prev);

    if (diff > 1) {
      // Instant jump: disable transition, move, then re-enable
      flushSync(() => {
        setSkipTransition(true);
        setDisplayStep(authPanel);
      });
      // After paint, re-enable for future moves
      requestAnimationFrame(() => {
        setSkipTransition(false);
      });
    } else {
      setSkipTransition(false);
      setDisplayStep(authPanel);
    }
  }, [authPanel]);

  /* ── Height animation ── */
  const [viewportHeight, setViewportHeight] = useState<number | undefined>(undefined);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const observerRef = useRef<ResizeObserver | null>(null);

  const setContentRef = (index: number) => (el: HTMLDivElement | null) => {
    contentRefs.current[index] = el;
  };

  const remeasure = () => {
    requestAnimationFrame(() => {
      const contentEl = contentRefs.current[authPanel];
      if (contentEl) {
        setViewportHeight(contentEl.offsetHeight);
      }
    });
  };

  // Use useLayoutEffect for the FIRST measurement on open so the height is
  // set BEFORE the browser paints — eliminates the "first-open hiccup" where
  // the modal would render at natural height (height: auto) then snap to the
  // measured pixel value ~50ms later. Subsequent panel changes still flow
  // through the ResizeObserver for continuous tracking.
  useLayoutEffect(() => {
    if (!open) return;

    observerRef.current?.disconnect();

    const contentEl = contentRefs.current[authPanel];
    if (!contentEl) return;

    const measure = () => {
      const h = contentEl.offsetHeight;
      if (h > 0) setViewportHeight(h);
    };

    // Synchronous first measurement before paint — kills the auto→fixed snap
    measure();

    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(contentEl);

    return () => observerRef.current?.disconnect();
  }, [authPanel, open]);

  useEffect(() => {
    if (!open) {
      // Intentionally NOT resetting viewportHeight to undefined here. The Dialog
      // fade-out takes ~225ms; flipping height to "auto" mid-fade snaps the modal
      // to the natural-height of all stacked panels and looks like a glitch.
      // Leaving the last measured height in place means the modal fades cleanly
      // at its current size. On next open, the [authPanel, open] effect above
      // re-measures the new panel synchronously before paint.
      return;
    }
    // No setTimeout fallback any more — useLayoutEffect above measures
    // synchronously before paint, so this branch is just a placeholder for
    // any future "on-open" side effects that aren't height-related.
  }, [open]);

  /* ── Title per panel ── */
  const titleKey = PANEL_TITLES[authPanel] || "accountLogin";
  const title = titleKey.includes(".") ? t(titleKey) : t(titleKey);

  /* ── Grace-state restore modal ── */
  const [restoreInfo, setRestoreInfo] = useState<{
    permanentDeleteOn: string | null;
    userEmail: string | null;
    hadSubscription: boolean;
    subEndsOn: string | null;
    sessionTokens: {
      accessToken: string;
      refreshToken: string;
      accessTokenExpiresAt?: string;
      refreshTokenExpiresAt?: string;
    };
  } | null>(null);

  // Scroll affordance: the slider viewport is the DEFINED scroll element on
  // phone/tablet (capped + overflow-y:auto below). Its MutationObserver catches
  // the late-loading captcha that pushes the body past the cap.
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const canScrollDown = useScrollAffordance(viewportRef, [open, isMobileOrTablet, authPanel, displayStep, viewportHeight]);
  // Only make the viewport a scroll body when the ACTIVE panel is actually taller
  // than the screen — otherwise the short login panel would inherit the signup
  // panel's height (the slider track is as tall as its tallest panel) and scroll
  // into empty space. Matches the `calc(100dvh - 140px)` cap below.
  const availableViewportPx = (typeof window !== "undefined" ? window.innerHeight : 800) - 140;
  // Phone create-account owns its OWN internal scroll body + fixed footer
  // (variant (a) — the submit button stays visible while the fields scroll), so
  // the slider viewport must NOT also scroll here, or we'd double-scroll.
  const phoneSignupActive = isPhone && authPanel === 1;
  const needsScroll =
    isMobileOrTablet && !phoneSignupActive && viewportHeight != null && viewportHeight > availableViewportPx;

  return (
    <>
    <GradientDialog
      open={open}
      handleClose={handleClose}
      isClosable={isClosable}
      isNotScrollable
      topBottomPadding="0px"
      leftRightPadding="0px"
      // Phone/tablet: the SLIDER VIEWPORT below is the defined scroll body
      // (capped + overflow-y:auto). Unlock the GradientDialog's own .scroll-wrapper
      // cap (it's `overflow:hidden` under isNotScrollable and would otherwise CLIP
      // our viewport's bottom — and the scroll hint with it).
      sx={
        isMobileOrTablet
          ? { "& .scroll-wrapper": { maxHeight: "none" } }
          : undefined
      }
    >
      <Stack width="100%">
        {/* ── Header (login/signup only) ── */}
        {authPanel <= 1 && (
          // Phone: icon LEFT + heading RIGHT in a single row, vertically
          // centred on the ✕ (top-right), with right padding reserving the
          // ✕'s space — saves the vertical height the old stacked layout cost.
          // Desktop keeps the centred logo-over-heading stack.
          <Stack
            direction={isPhone ? "row" : "column"}
            // Desktop: the logo sits close to the heading — they are one mark,
            // not two stacked elements.
            spacing={isPhone ? "0" : "6px"}
            alignItems="center"
            justifyContent="center"
            // Phone: symmetric 52px side padding reserves the logo (left) + ✕
            // (right) so the heading centres on the true MODAL WIDTH.
            padding={isPhone ? "0 52px" : "32px 32px 0"}
            // Phone: 68px tall so the centred heading lines up vertically with
            // the ✕ (top:16 + 36/2 = centre at 34px = half of 68).
            sx={isPhone ? { minHeight: "68px" } : undefined}
          >
            {/* Phone: no logo — just the centred heading. Desktop keeps the
                logo-over-heading stack. */}
            {!isPhone && <Logo src={AudioSanctumLogo} alt="Logo" loading="lazy" />}
            <DialogTitleTypography style={isPhone ? { textAlign: "center" } : undefined}>{title}</DialogTitleTypography>
          </Stack>
        )}

        {/* ── Slider ── */}
        <div style={{ position: "relative", width: "100%" }}>
        <AuthSliderViewport
          ref={viewportRef}
          style={{
            height: viewportHeight || "auto",
            // Cap to the visible viewport + scroll ONLY when the active panel
            // overflows (signup-with-captcha on small screens). Login keeps its
            // natural height (overflow hidden via the styled component).
            ...(needsScroll ? { maxHeight: "calc(100dvh - 140px)", overflowY: "auto", overflowX: "hidden" } : {}),
          }}
        >
          <AuthSliderTrack $step={displayStep} $skip={skipTransition}>
            {/* Panel 0: Login */}
            <AuthSlidePanel>
              <div ref={setContentRef(0)}>
                {/* Phone: 24px side inset (matches the Create-Account panel) so
                    the login email/password boxes are the same width. Reduced top
                    padding so the email field sits a bit closer to the heading. */}
                <Stack padding={isPhone ? "8px 24px 32px" : "10px 32px 32px"} spacing="16px">
                  <Form
                    isSignUp={false}
                    handleClose={handleClose}
                    onForgotPassword={() => setAuthPanel(2)}
                    onLayoutChange={remeasure}
                    loginNotice={effectiveNotice}
                    onAccountScheduledForDeletion={(info) => {
                      // Close the login dialog and surface the blocking
                      // restore modal. The user's session tokens (issued
                      // by the backend as part of the grace-state error
                      // response) are passed through so the restore POST
                      // can authenticate.
                      handleClose();
                      setRestoreInfo(info);
                    }}
                  />
                  <Stack alignItems="center">
                    <InfoLink underline="hover" onClick={() => setAuthPanel(1)}>
                      {t("createAccountButton")}
                    </InfoLink>
                  </Stack>
                </Stack>
              </div>
            </AuthSlidePanel>

            {/* Panel 1: Create Account */}
            <AuthSlidePanel>
              <div ref={setContentRef(1)}>
                <Stack padding={isPhone ? "8px 24px 20px" : "16px 32px 32px"} spacing={isPhone ? "12px" : "16px"}>
                  <Form
                    isSignUp
                    handleClose={handleClose}
                    onForgotPassword={() => setAuthPanel(2)}
                    onLayoutChange={remeasure}
                    loginNotice=""
                    onSwitchToLogin={() => setAuthPanel(0)}
                  />
                  {/* The link lives in the Form's own fixed footer, on every
                      size — it used to be phone-only, and the copy kept here for
                      desktop became a second one under the first. */}
                </Stack>
              </div>
            </AuthSlidePanel>

            {/* Panel 2: Recovery Step 1 — Enter email */}
            <AuthSlidePanel>
              <div ref={setContentRef(2)}>
                <Stack padding="32px 32px" spacing="16px">
                  <FirstStep handleNextStep={() => setAuthPanel(3)} />
                  <Stack alignItems="center">
                    <InfoLink underline="hover" onClick={() => setAuthPanel(0)}>
                      Back to login
                    </InfoLink>
                  </Stack>
                </Stack>
              </div>
            </AuthSlidePanel>

            {/* Panel 3: Recovery Step 2 — Email sent */}
            <AuthSlidePanel>
              <div ref={setContentRef(3)}>
                <Stack padding="32px 32px" spacing="16px">
                  <SecondStep />
                </Stack>
              </div>
            </AuthSlidePanel>

            {/* Panel 4: Recovery Step 3 — Reset password (via token) */}
            <AuthSlidePanel>
              <div ref={setContentRef(4)}>
                <Stack padding="32px 32px" spacing="16px">
                  {/* On successful reset, swing back to the login panel and
                      pick up the success notice the recovery Form just
                      stashed in sessionStorage. Stays inside the dialog so
                      the user can log in with the new password without an
                      extra page navigation. */}
                  <ThirdStep
                    handleNextStep={() => {
                      refreshLoginNoticeFromStorage();
                      setAuthPanel(0);
                    }}
                    resetToken={resetToken}
                    onRequestNewLink={() => setAuthPanel(2)}
                    onLayoutChange={remeasure}
                  />
                </Stack>
              </div>
            </AuthSlidePanel>
          </AuthSliderTrack>
        </AuthSliderViewport>

        {/* "Scroll ⌄" hint — only when the active panel scrolls (signup-tall),
            absolutely anchored to the scroll body's bottom edge. */}
        {needsScroll && (
          <AuthScrollFade $visible={canScrollDown} aria-hidden="true">
            <ScrollHint>
              <span>Scroll</span>
              <ScrollChevron />
            </ScrollHint>
          </AuthScrollFade>
        )}
        </div>
      </Stack>
    </GradientDialog>
    <AccountRestoreModal
      open={!!restoreInfo}
      permanentDeleteOn={restoreInfo?.permanentDeleteOn ?? null}
      userEmail={restoreInfo?.userEmail ?? null}
      hadSubscription={!!restoreInfo?.hadSubscription}
      subEndsOn={restoreInfo?.subEndsOn ?? null}
      sessionTokens={restoreInfo?.sessionTokens ?? null}
      onClose={() => setRestoreInfo(null)}
    />
    </>
  );
};

export default LoginDialog;
